/**
 * @deprecated This file is deprecated in favor of ai.service.js
 * 
 * MIGRATION NOTICE:
 * This is the legacy AI service with simpler API interface.
 * Please use ai.service.js instead which provides:
 * - Enhanced features with credit management
 * - Better error handling and security
 * - Comprehensive logging and monitoring
 * - Integration with credit.service.js
 * 
 * Current Usage (to be migrated):
 * - DatabasePage.jsx (line 9) - imports searchCandidatesWithAI
 * - ApplicationsPage.jsx (line 16) - imports screenApplicantsWithAI
 * 
 * This file is kept temporarily for backward compatibility.
 * TODO: Update DatabasePage and ApplicationsPage to use ai.service.js
 */

import api from '../api/api';
import { API_URL } from '../config/constants';

// Show deprecation warning in development
if (process.env.NODE_ENV === 'development') {
  console.warn(
    '%c[DEPRECATED] aiService.js',
    'color: orange; font-weight: bold',
    '\nThis service is deprecated. Please use ai.service.js instead.'
  );
}

// Configuration constants
const API_BASE_URL = API_URL;
const OPENAI_MODELS = {
  STANDARD: 'gpt-3.5-turbo', // For Starter/Basic plans
  PREMIUM: 'gpt-4o',         // For Pro/Elite plans
};

// Maximum tokens for different operations
const MAX_TOKENS = {
  SEARCH: 1000,
  SCREENING: 1500
};

/**
 * Determine which OpenAI model to use based on the user's plan
 * @param {string} planType - User's subscription plan (starter, basic, pro, elite)
 * @returns {string} - The appropriate OpenAI model
 */
const getModelForPlan = (planType) => {
  const plan = planType.toLowerCase();
  if (plan === 'pro' || plan === 'elite') {
    return OPENAI_MODELS.PREMIUM;
  }
  return OPENAI_MODELS.STANDARD;
};

/**
 * Search for candidates in the database using AI
 * @param {Object} searchParams - Search parameters
 * @param {string} searchParams.jobDescription - Job description or search intent
 * @param {number} searchParams.maxResults - Maximum number of results to return
 * @param {string} searchParams.planType - User's subscription plan
 * @param {number} searchParams.availableCredits - Available AI credits
 * @returns {Promise} - Promise that resolves with search results
 */
export const searchCandidatesWithAI = async (searchParams) => {
  try {
    const { jobDescription, maxResults = 20, planType, availableCredits } = searchParams;
    
    // Check if user has enough credits
    if (availableCredits <= 0) {
      return {
        success: false,
        error: 'Insufficient AI credits',
        errorCode: 'INSUFFICIENT_CREDITS'
      };
    }
    
    // Call backend API with authentication
    // The api instance automatically includes auth token
    const response = await api.post('/ai/search', {
      jobDescription,
      maxResults,
      model: getModelForPlan(planType),
      maxTokens: MAX_TOKENS.SEARCH
    });

    // Extract data from response (handle PHP wrapper structure)
    const responseData = response.data?.data || response.data;

    // Ensure candidates array exists
    if (!responseData.candidates || !Array.isArray(responseData.candidates)) {
      console.error('❌ Invalid response structure - no candidates array found:', response.data);
      console.error('Response data keys:', Object.keys(responseData));
      return {
        success: false,
        error: 'Invalid response from server - no candidates found',
        errorCode: 'INVALID_RESPONSE'
      };
    }

    // Process and format the results
    const candidates = responseData.candidates.map(candidate => ({
      ...candidate,
      matchScore: Math.round(candidate.matchScore * 100) / 100, // Round to 2 decimal places
    }));
    
    // Backend already filters at 60%+ threshold, no need to filter again
    // Just sort by match score (highest first)
    const sortedCandidates = candidates.sort((a, b) => b.matchScore - a.matchScore);
    
    return {
      success: true,
      data: {
        candidates: sortedCandidates,
        creditsUsed: responseData.credits?.used || responseData.profilesProcessed || sortedCandidates.length,
        totalProfilesProcessed: responseData.profilesProcessed,
        timestamp: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error in AI candidate search:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to perform AI search',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'
    };
  }
};

/**
 * Screen job applicants using AI
 * @param {Object} screeningParams - Screening parameters
 * @param {string} screeningParams.jobId - Job ID
 * @param {Array} screeningParams.applicants - List of applicants to screen
 * @param {string} screeningParams.jobDescription - Job description
 * @param {Object} screeningParams.requirements - Job requirements
 * @param {string} screeningParams.planType - User's subscription plan
 * @param {number} screeningParams.availableCredits - Available AI credits
 * @returns {Promise} - Promise that resolves with screening results
 */
export const screenApplicantsWithAI = async (screeningParams) => {
  try {
    const { 
      jobId, 
      applicants, 
      jobDescription, 
      requirements, 
      planType, 
      availableCredits 
    } = screeningParams;
    
    // Check if user has enough credits
    if (availableCredits < applicants.length) {
      return {
        success: false,
        error: 'Insufficient AI credits',
        errorCode: 'INSUFFICIENT_CREDITS',
        requiredCredits: applicants.length,
        availableCredits};
    }
    
    // Call backend API with authentication
    // The api instance automatically includes auth token
    const response = await api.post('/ai/screen', {
      jobId,
      applicants,
      jobDescription,
      requirements,
      model: getModelForPlan(planType),
      maxTokens: MAX_TOKENS.SCREENING});
    
    // Extract data from response (handle PHP wrapper structure)
    const responseData = response.data?.data || response.data;
    
    // Ensure screenedApplicants array exists
    if (!responseData.screenedApplicants || !Array.isArray(responseData.screenedApplicants)) {
      console.error('Invalid response structure:', response.data);
      return {
        success: false,
        error: 'Invalid response from server',
        errorCode: 'INVALID_RESPONSE'};
    }
    
    // Process and format the results
    const screenedApplicants = responseData.screenedApplicants.map(applicant => ({
      ...applicant,
      score: Math.round(applicant.score * 100) / 100, // Round to 2 decimal places
    }));
    
    // Sort by score (highest first)
    const sortedApplicants = screenedApplicants.sort((a, b) => b.score - a.score);
    
    return {
      success: true,
      data: {
        screenedApplicants: sortedApplicants,
        creditsUsed: responseData.credits?.used || applicants.length,
        timestamp: new Date().toISOString(),
        jobId: responseData.jobId || jobId}
    };
  } catch (error) {
    console.error('Error in AI applicant screening:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to perform AI screening',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Generate an optimized prompt for OpenAI based on the operation type
 * @param {string} operationType - Type of operation ('search' or 'screening')
 * @param {Object} data - Data to include in the prompt
 * @returns {Object} - Formatted prompt for OpenAI API
 */
export const generatePrompt = (operationType, data) => {
  switch (operationType) {
    case 'search':
      return {
        system: `You are an AI recruiter assistant that helps match job descriptions with candidate profiles. 
                Analyze the job description and each candidate profile to determine compatibility.
                For each candidate, provide a match score from 0-100 and explain why they match or don't match.
                Focus on skills, experience, education, and other relevant factors.`,
        user: JSON.stringify({
          jobDescription: data.jobDescription,
          candidates: data.candidates})};
    
    case 'screening':
      return {
        system: `You are an AI recruiter assistant that screens job applicants.
                Analyze each applicant's resume against the job requirements to determine suitability.
                For each applicant, provide a score from 0-100 and explain why they are suitable or not.
                Flag any edge cases such as missing key requirements or being overqualified.`,
        user: JSON.stringify({
          jobDescription: data.jobDescription,
          requirements: data.requirements,
          applicants: data.applicants})};
    
    default:
      throw new Error(`Unknown operation type: ${operationType}`);
  }
};

/**
 * Parse and validate AI response
 * @param {string} response - Raw response from OpenAI
 * @param {string} operationType - Type of operation ('search' or 'screening')
 * @returns {Object} - Parsed and validated response
 */
export const parseAIResponse = (response, operationType) => {
  try {
    // Extract JSON from response (in case it's wrapped in markdown or other text)
    const jsonMatch = response.match(/```json\n([\s\S]*?)\n```/) || 
                      response.match(/\{[\s\S]*\}/);
    
    const jsonStr = jsonMatch ? jsonMatch[1] || jsonMatch[0] : response;
    const parsedResponse = JSON.parse(jsonStr);
    
    // Validate based on operation type
    switch (operationType) {
      case 'search':
        if (!Array.isArray(parsedResponse.candidates)) {
          throw new Error('Invalid response format: candidates array missing');
        }
        break;
      
      case 'screening':
        if (!Array.isArray(parsedResponse.screenedApplicants)) {
          throw new Error('Invalid response format: screenedApplicants array missing');
        }
        break;
      
      default:
        throw new Error(`Unknown operation type: ${operationType}`);
    }
    
    return parsedResponse;
  } catch (error) {
    console.error('Error parsing AI response:', error);
    throw new Error('Failed to parse AI response');
  }
};

export default {
  searchCandidatesWithAI,
  screenApplicantsWithAI,
  getModelForPlan,
  generatePrompt,
  parseAIResponse,
  OPENAI_MODELS,
  MAX_TOKENS};
